import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  PlusCircle,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  delete_menu_data,
  get_addon_data_res,
  server_post_data,
} from "../ServiceConnection/serviceconnection";

export default function ItemAddons() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // State management
  const [searchQuery, setSearchQuery] = useState("");
  const [addons, setAddons] = useState([]);
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);

  // Dialog states
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isToggleDialogOpen, setIsToggleDialogOpen] = useState(false);
  const [viewingAddon, setViewingAddon] = useState(null);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [statusChangeMessage, setStatusChangeMessage] = useState("");
  const [loading, setLoading] = useState(true);

  // Filter addons
  const filteredAddons = useCallback(() => {
    if (!addons || addons.length === 0) return [];

    const query = searchQuery.toLowerCase();
    return addons.filter((addon) => {
      return (
        addon.addon_name.toLowerCase().includes(query) ||
        addon.cate_name?.toLowerCase().includes(query)
      );
    });
  }, [addons, searchQuery])();

  // Fetch addons data
  const fetchAddons = useCallback(async () => {
    setShowLoaderAdmin(true);
    setLoading(true);
    const fd = new FormData();
    fd.append("flag", 1);

    try {
      const response = await server_post_data(get_addon_data_res, fd);

      if (!response.data.error) {
        setAddons(response.data.message || []);
      } else {
        setAddons([]);
      }
    } catch (error) {
      console.error("Error fetching addons:", error);
      setAddons([]);
    } finally {
      setShowLoaderAdmin(false);
      setLoading(false);
    }
  }, []);

  // Handle view
  const handleViewClick = useCallback((addon) => {
    setViewingAddon(addon);
    setIsViewDialogOpen(true);
  }, []);

  // Handle edit
  const handleEditClick = useCallback(
    (addon) => {
      navigate("/edit-addon", { state: { primary_id: addon.primary_id } });
    },
    [navigate],
  );

  // Handle delete
  const handleDeleteClick = useCallback((addon) => {
    setSelectedAddon(addon);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedAddon) return;

    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("id_for_delete", selectedAddon.primary_id);
    fd.append("flag_for", "4");

    try {
      const response = await server_post_data(delete_menu_data, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to delete addon",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Deleted",
          description: `${selectedAddon.addon_name} has been deleted successfully`,
        });
        fetchAddons();
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsDeleteDialogOpen(false);
      setSelectedAddon(null);
    }
  }, [selectedAddon, toast, fetchAddons]);

  // Handle toggle status
  const handleToggleClick = useCallback((addon) => {
    setSelectedAddon(addon);
    setStatusChangeMessage(
      addon.active_status === 1
        ? "Are you sure you want to deactivate this addon?"
        : "Are you sure you want to activate this addon?",
    );
    setIsToggleDialogOpen(true);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!selectedAddon) return;

    setShowLoaderAdmin(true);
    const newStatus = selectedAddon.active_status === 1 ? 0 : 1;

    const fd = new FormData();
    fd.append("id_for_delete", selectedAddon.primary_id);
    fd.append("active_status", newStatus);
    fd.append("flag_check", 1);
    fd.append("flag_for", "4");

    try {
      const response = await server_post_data(delete_menu_data, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to update status",
          variant: "destructive",
        });
      } else {
        setAddons((prev) =>
          prev.map((item) =>
            item.primary_id === selectedAddon.primary_id
              ? { ...item, active_status: newStatus }
              : item,
          ),
        );
        toast({
          title: "Success",
          description: `Addon ${
            newStatus === 1 ? "activated" : "deactivated"
          } successfully`,
        });
      }
    } catch (error) {
      console.error("Toggle error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsToggleDialogOpen(false);
      setSelectedAddon(null);
    }
  }, [selectedAddon, toast]);

  // Get food type badge color
  const getFoodTypeBadge = (type) => {
    const types = {
      Veg: "bg-success/20 text-success border-success/30",
      "Non-Veg": "bg-destructive/20 text-destructive border-destructive/30",
      Egg: "bg-warning/20 text-warning border-warning/30",
      "Egg-Free": "bg-primary/20 text-primary border-primary/30",
      Vegan: "bg-success/20 text-success border-success/30",
      "Dairy-Free": "bg-primary/20 text-primary border-primary/30",
      "Sugar-Free": "bg-primary/20 text-primary border-primary/30",
      "Gluten-Free": "bg-primary/20 text-primary border-primary/30",
      Halal: "bg-primary/20 text-primary border-primary/30",
      "Low-Carb": "bg-primary/20 text-primary border-primary/30",
      Spicy: "bg-destructive/20 text-destructive border-destructive/30",
    };
    return types[type] || "bg-muted text-muted-foreground";
  };

  // Initial load
  useEffect(() => {
    fetchAddons();
  }, [fetchAddons]);

  return (
    <MainLayout>
      <Header
        title="Item Addons Management"
        subtitle="Manage menu item addons and customizations"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <PlusCircle className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {addons.length}
                </p>
                <p className="text-sm text-muted-foreground">Total Addons</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {addons.filter((a) => a.active_status === 1).length}
                </p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <XCircle className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {addons.filter((a) => a.active_status === 0).length}
                </p>
                <p className="text-sm text-muted-foreground">Inactive</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search addons..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            onClick={() => navigate("/add-addon")}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            Add Addon
          </Button>
        </div>

        {/* Addons Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Sr.No</TableHead>
                <TableHead className="text-muted-foreground">
                  Addon Name
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Categories
                </TableHead>
                <TableHead className="text-muted-foreground">Min/Max</TableHead>
                <TableHead className="text-muted-foreground">
                  Total Items
                </TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-12 text-muted-foreground"
                  >
                    Loading Addons...
                  </TableCell>
                </TableRow>
              ) : filteredAddons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <PlusCircle className="w-8 h-8" />
                      <p>No addons found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAddons.map((addon, index) => (
                  <TableRow
                    key={addon.primary_id}
                    className="border-border hover:bg-muted/30"
                  >
                    <TableCell className="font-medium">{index + 1}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                          <PlusCircle className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <p className="font-medium text-foreground">
                          {addon.addon_name}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {addon.cate_name || "N/A"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-normal">
                          Min: {addon.addon_min}
                        </Badge>
                        <Badge variant="outline" className="font-normal">
                          Max: {addon.addon_max}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-normal">
                        {addon.items?.length || 0} items
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleToggleClick(addon)}
                        className={cn(
                          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                          addon.active_status === 1
                            ? "bg-success"
                            : "bg-muted-foreground/30",
                        )}
                      >
                        <span
                          className={cn(
                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                            addon.active_status === 1
                              ? "translate-x-6"
                              : "translate-x-1",
                          )}
                        />
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewClick(addon)}
                          className="hover:bg-primary/10 hover:text-primary"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(addon)}
                          className="hover:bg-primary/10 hover:text-primary"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(addon)}
                          className="hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* View Addon Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Addon Details</DialogTitle>
              <DialogDescription>
                View all items in this addon group
              </DialogDescription>
            </DialogHeader>

            {viewingAddon && (
              <div className="space-y-6 py-4">
                {/* Addon Header */}
                <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                    <PlusCircle className="h-8 w-8 text-primary-foreground" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-foreground">
                      {viewingAddon.addon_name}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {viewingAddon.cate_name}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="outline" className="font-normal">
                      Min: {viewingAddon.addon_min}
                    </Badge>
                    <Badge variant="outline" className="font-normal">
                      Max: {viewingAddon.addon_max}
                    </Badge>
                  </div>
                </div>

                {/* Addon Items Table */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground">
                    Addon Items ({viewingAddon.items?.length || 0})
                  </h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="text-muted-foreground">
                            Item Name
                          </TableHead>
                          <TableHead className="text-muted-foreground">
                            Price
                          </TableHead>
                          <TableHead className="text-muted-foreground">
                            Type
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {viewingAddon.items && viewingAddon.items.length > 0 ? (
                          viewingAddon.items.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">
                                {item.item}
                              </TableCell>
                              <TableCell>₹{item.price}</TableCell>
                              <TableCell>
                                <Badge
                                  className={cn(
                                    "border font-normal",
                                    getFoodTypeBadge(item.type),
                                  )}
                                >
                                  {item.type}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={3}
                              className="text-center text-muted-foreground"
                            >
                              No items available
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsViewDialogOpen(false)}
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setIsViewDialogOpen(false);
                  handleEditClick(viewingAddon);
                }}
                className="gradient-primary text-primary-foreground hover:opacity-90"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Addon
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Addon</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "
                <strong>{selectedAddon?.addon_name}</strong>"? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                disabled={showLoaderAdmin}
                className="bg-destructive hover:bg-destructive/90"
              >
                {showLoaderAdmin ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Toggle Status Confirmation Dialog */}
        <AlertDialog
          open={isToggleDialogOpen}
          onOpenChange={setIsToggleDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Addon Status</AlertDialogTitle>
              <AlertDialogDescription>
                {statusChangeMessage}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmToggle}
                disabled={showLoaderAdmin}
                className="gradient-primary text-primary-foreground hover:opacity-90"
              >
                {showLoaderAdmin ? "Updating..." : "Confirm"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}
